import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, qs } from "@/lib/api";
import { addDays, formatCents, formatDate, formatDay, mountainDay } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import type { Product } from "./catalog";
import { useContactNames } from "./useContactNames";

export interface Subscription {
  id: number;
  keap_id: number | null;
  contact_id: number;
  brand_id: number;
  product_id: number;
  price_id: number | null;
  status: "trialing" | "active" | "past_due" | "paused" | "cancelled" | "ended" | string;
  amount_cents: number;
  interval: string;
  interval_count: number;
  payment_method_id: number | null;
  billing_enabled: boolean;
  /** false = manual pay: renewals make an invoice and email a pay link, and never charge a card. */
  auto_charge?: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  next_bill_date: string | null;
  trial_ends_on: string | null;
  cancel_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  ended_at: string | null;
  source: string;
  started_at: string;
  pending_price_id: number | null;
  past_due_since: string | null;
  access_suspended_at: string | null;
  product_name: string;
  card?: string;
  summary: string;
  open_invoices: { id: number; number: string; status: string; amount_due_cents: number; amount_paid_cents: number; due_date: string | null; description: string | null }[];
  access: { id: number; access_key: string; source: string; ends_at: string | null; revoked_at: string | null }[];
}

export const SUB_STATUS: Record<string, { label: string; variant: "success" | "warning" | "destructive" | "muted" | "default" }> = {
  trialing: { label: "trial", variant: "default" },
  active: { label: "active", variant: "success" },
  past_due: { label: "past due", variant: "destructive" },
  paused: { label: "paused", variant: "warning" },
  cancelled: { label: "cancelled", variant: "muted" },
  ended: { label: "ended", variant: "muted" },
};

export const every = (s: { interval: string; interval_count: number | null }) => ((s.interval_count ?? 1) > 1 ? `every ${s.interval_count} ${s.interval}s` : `a ${s.interval}`);

const WITHIN: [string, string][] = [
  ["", "Any next charge"],
  ["7", "Next charge within 7 days"],
  ["30", "Within 30 days"],
  ["overdue", "Next charge date has passed"],
  ["none", "No next charge"],
];

/** Billing → Subscriptions: who pays every month or year, what's next, and who's behind. */
export function SubscriptionsPage() {
  const [status, setStatus] = useState("active");
  const [product, setProduct] = useState<number | "">("");
  const [within, setWithin] = useState("");
  const q = useQuery({
    queryKey: ["subscriptions", status, product],
    queryFn: () => api<{ subscriptions: Subscription[] }>("/api/v1/subscriptions" + qs({ status: status || undefined, product_id: product || undefined, limit: 500 })),
    placeholderData: (p) => p,
  });
  const products = useQuery({ queryKey: ["products", "all"], queryFn: () => api<{ products: Product[] }>("/api/v1/products" + qs({ limit: 500 })), staleTime: 60_000 });
  const plans = (products.data?.products ?? []).filter((p) => p.prices.some((x) => x.type === "recurring"));
  const today = mountainDay();
  const rows = useMemo(() => {
    const all = q.data?.subscriptions ?? [];
    return all.filter((s) => {
      const day = s.next_bill_date?.slice(0, 10) ?? "";
      if (within === "none") return !day;
      if (within === "overdue") return !!day && day < today;
      if (within) return !!day && day >= today && day <= addDays(today, Number(within));
      return true;
    });
  }, [q.data, within, today]);
  const name = useContactNames(rows.map((s) => s.contact_id));
  const total = q.data?.subscriptions.length ?? 0;
  return (
    <div className="mx-auto max-w-[1200px] space-y-3">
      <PageHeader title="Subscriptions" sub="Memberships billed every month or year">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Imported Keap subscriptions show "charged in Keap" until the go-live switch turns CRM billing on for them. Runs charge at 7 AM, 1 PM and 7 PM Mountain Time.
        </p>
      </PageHeader>
      <Card className="overflow-x-auto">
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <Select className="h-8 w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="">Any status</option>
            {Object.entries(SUB_STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </Select>
          <Select className="h-8 w-auto" value={product} onChange={(e) => setProduct(e.target.value ? Number(e.target.value) : "")} aria-label="Plan">
            <option value="">Every plan</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Select className="h-8 w-auto" value={within} onChange={(e) => setWithin(e.target.value)} aria-label="Next charge">
            {WITHIN.map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
          <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">
            {rows.length === total ? `${total}` : `${rows.length} of ${total}`} shown{total >= 500 ? " (first 500)" : ""}
          </span>
        </div>
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {!q.isLoading && rows.length === 0 && <Empty title="No subscriptions match." />}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Person</TH>
                <TH>Plan</TH>
                <TH className="text-right">Amount</TH>
                <TH>Status</TH>
                <TH>Next charge</TH>
                <TH>Card</TH>
              </tr>
            </THead>
            <tbody>
              {rows.map((s) => {
                const st = SUB_STATUS[s.status] ?? { label: s.status, variant: "muted" as const };
                return (
                  <TR key={s.id}>
                    <TD className="max-w-[14rem]">
                      <Link to="/billing/subscriptions/$subscriptionId" params={{ subscriptionId: String(s.id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
                        {name(s.contact_id)}
                      </Link>
                      <div className="text-xs text-[var(--color-muted-foreground)]">since {formatDate(s.started_at)}</div>
                    </TD>
                    <TD className="max-w-[16rem]">
                      <span className="flex items-center gap-1.5">
                        <BrandMark id={s.brand_id} /> <span className="truncate">{s.product_name}</span>
                      </span>
                    </TD>
                    <TD className="num whitespace-nowrap text-right">
                      {formatCents(s.amount_cents)} <span className="text-xs text-[var(--color-muted-foreground)]">{every(s)}</span>
                    </TD>
                    <TD>
                      <span className="flex flex-wrap gap-1">
                        <Badge variant={st.variant}>{st.label}</Badge>
                        {!s.billing_enabled && (
                          <Badge variant="muted" title="Imported from Keap: Keap still charges it until the go-live switch">
                            charged in Keap
                          </Badge>
                        )}
                        {s.auto_charge === false && (
                          <Badge variant="outline" title="Manual pay: each renewal makes an invoice and emails a pay link; no card is charged">
                            manual pay
                          </Badge>
                        )}
                        {s.cancel_at && !s.ended_at && <Badge variant="warning">ends {formatDay(s.cancel_at)}</Badge>}
                      </span>
                    </TD>
                    <TD className="num whitespace-nowrap text-xs">
                      {s.next_bill_date ? formatDay(s.next_bill_date) : "—"}
                      {s.past_due_since && <div className="text-[var(--color-danger-fg)]">past due since {formatDay(s.past_due_since)}</div>}
                    </TD>
                    <TD className="text-xs" title={s.auto_charge === false ? "Manual pay: renewals email a pay link" : s.card ? undefined : "No card of its own: billing charges the person's default card"}>
                      {s.auto_charge === false ? (
                        "pay link"
                      ) : (
                        s.card || (s.payment_method_id ? "saved card" : <span className="text-[var(--color-muted-foreground)]">default card</span>)
                      )}
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
